import React, { useEffect, useState, useRef } from 'react';
import { Plus, AlertTriangle, FileText, Newspaper, Cloud, Server, Database, ChevronRight, Shield, Building } from 'lucide-react';
import type { Environment } from './RecentScans';
interface QuickActionsProps {
  onStartScan: () => void;
  onViewVulnerabilities: () => void;
  onGenerateReport: (env: Environment) => void;
  onViewBulletins: () => void;
  onAddClient?: () => void;
  environments?: Environment[];
  onViewClientVulns?: (env: Environment) => void;
}
export function QuickActions({
  onStartScan,
  onViewVulnerabilities,
  onGenerateReport,
  onViewBulletins,
  onAddClient,
  environments = [],
  onViewClientVulns
}: QuickActionsProps) {
  const [activePickerId, setActivePickerId] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setActivePickerId(null);
      }
    };
    if (activePickerId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePickerId]);
  const getStatusColor = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-astro-green';
      case 'warning':
        return 'bg-yellow-400';
      case 'critical':
        return 'bg-astro-danger';
      default:
        return 'bg-astro-text-secondary';
    }
  };
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={11} data-id="element-520" />;
      case 'On-Prem':
        return <Server size={11} data-id="element-521" />;
      case 'Hybrid':
        return <Database size={11} data-id="element-522" />;
      default:
        return <Server size={11} data-id="element-523" />;
    }
  };
  const actions = [{
    id: 'add-client',
    icon: <Building size={18} className="text-white" data-id="element-524" />,
    iconBg: 'bg-blue-600',
    title: 'Add Client',
    subtitle: 'Onboard a new client or your own infra',
    onClick: onAddClient || (() => {}),
    hasEnvPicker: false
  }, {
    id: 'scan',
    icon: <Plus size={18} className="text-white" data-id="element-525" />,
    iconBg: 'bg-astro-green-dark',
    title: 'Run New Scan',
    subtitle: 'Start a fresh security assessment',
    onClick: onStartScan,
    hasEnvPicker: false
  }, {
    id: 'vulns',
    icon: <AlertTriangle size={18} className="text-astro-green" data-id="element-526" />,
    iconBg: 'bg-astro-green-dim border border-astro-border',
    title: 'View Vulnerabilities',
    subtitle: 'Select an environment to review',
    onClick: () => {
      if (environments.length > 0 && onViewClientVulns) {
        setActivePickerId(activePickerId === 'vulns' ? null : 'vulns');
      } else {
        onViewVulnerabilities();
      }
    },
    hasEnvPicker: true,
    onEnvSelect: (env: Environment) => onViewClientVulns?.(env)
  }, {
    id: 'report',
    icon: <FileText size={18} className="text-astro-green" data-id="element-527" />,
    iconBg: 'bg-astro-green-dim border border-astro-border',
    title: 'Generate Report',
    subtitle: 'Create client security report',
    onClick: () => {
      if (environments.length > 0) {
        setActivePickerId(activePickerId === 'report' ? null : 'report');
      }
    },
    hasEnvPicker: true,
    onEnvSelect: (env: Environment) => onGenerateReport(env)
  }, {
    id: 'bulletins',
    icon: <Newspaper size={18} className="text-astro-green" data-id="element-528" />,
    iconBg: 'bg-astro-green-dim border border-astro-border',
    title: 'View Bulletins',
    subtitle: 'Latest threat intelligence',
    onClick: onViewBulletins,
    hasEnvPicker: false
  }];

  // Sort environments: critical first, then warning, then healthy
  const sortedEnvs = [...environments].sort((a, b) => {
    const order = {
      critical: 0,
      warning: 1,
      healthy: 2
    };
    return (order[a.status] ?? 2) - (order[b.status] ?? 2);
  });
  return <div className="bg-astro-card border border-astro-border rounded-xl p-6 h-full" data-id="element-529">
      <div className="mb-6" data-id="element-530">
        <h2 className="text-lg font-bold text-white" data-id="element-531">Quick Actions</h2>
        <p className="text-sm text-astro-text-secondary" data-id="element-532">
          Common tasks and navigation
        </p>
      </div>

      <div className="space-y-3" data-id="element-533">
        {actions.map(action => <div key={action.id} className="relative" ref={action.hasEnvPicker && activePickerId === action.id ? pickerRef : undefined} data-id="element-534">

            <button onClick={action.onClick} className={`w-full flex items-center gap-4 p-4 border rounded-lg transition-all group text-left ${action.hasEnvPicker && activePickerId === action.id ? 'bg-[#1a222c] border-astro-green/30' : 'border-astro-border hover:bg-[#1a222c]'}`} data-id="element-535">

              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${action.iconBg}`} data-id="element-536">

                {action.icon}
              </div>
              <div className="flex-1" data-id="element-537">
                <h3 className="text-sm font-bold text-white group-hover:text-astro-green transition-colors" data-id="element-538">
                  {action.title}
                </h3>
                <p className="text-xs text-astro-text-secondary" data-id="element-539">
                  {action.subtitle}
                </p>
              </div>
              {action.hasEnvPicker && environments.length > 0 && <ChevronRight size={14} className={`text-astro-text-secondary transition-transform duration-200 ${activePickerId === action.id ? 'rotate-90' : ''}`} data-id="element-540" />}
            </button>

            {/* Environment Picker Dropdown */}
            {action.hasEnvPicker && activePickerId === action.id && <div className="mt-1 bg-[#0d1117] border border-astro-border rounded-lg overflow-hidden shadow-xl shadow-black/40 animate-in fade-in slide-in-from-top-2 duration-150 z-20 relative" data-id="element-541">
                <div className="px-3 py-2 border-b border-astro-border" data-id="element-542">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-astro-text-secondary" data-id="element-543">
                    Select Environment
                  </span>
                </div>
                <div className="max-h-[240px] overflow-y-auto" data-id="element-544">
                  {sortedEnvs.map(env => <button key={env.id} onClick={e => {
              e.stopPropagation();
              setActivePickerId(null);
              action.onEnvSelect?.(env);
            }} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left group/env" data-id="element-545">

                      <div className={`w-2 h-2 rounded-full shrink-0 ${getStatusColor(env.status)}`} data-id="element-546">
                </div>
                      <div className="flex-1 min-w-0" data-id="element-547">
                        <div className="flex items-center gap-2" data-id="element-548">
                          <span className="text-sm font-medium text-white truncate group-hover/env:text-astro-green transition-colors" data-id="element-549">
                            {env.clientName}
                          </span>
                          <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-astro-text-secondary/60 shrink-0" data-id="element-550">
                            {getTypeIcon(env.type)}
                            {env.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-551">
                          <span className="text-[10px] text-astro-text-secondary" data-id="element-552">
                            {env.overPrivileged} over-privileged
                          </span>
                          <span className="text-astro-border text-[10px]" data-id="element-553">
                            ·
                          </span>
                          <span className={`text-[10px] font-mono ${env.complianceScore > 85 ? 'text-astro-green' : env.complianceScore > 70 ? 'text-astro-text-secondary' : 'text-astro-danger'}`} data-id="element-554">

                            {env.complianceScore}% compliant
                          </span>
                        </div>
                      </div>
                      {env.status !== 'healthy' && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${env.status === 'critical' ? 'bg-astro-danger/15 text-astro-danger' : 'bg-yellow-400/10 text-yellow-400'}`} data-id="element-555">

                          {env.status === 'critical' ? 'Critical' : 'Warning'}
                        </span>}
                    </button>)}
                </div>
                <div className="border-t border-astro-border" data-id="element-556">
                  <button onClick={e => {
              e.stopPropagation();
              setActivePickerId(null);
              onViewVulnerabilities();
            }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium text-astro-green hover:bg-astro-green/5 transition-colors" data-id="element-557">

                    <Shield size={12} data-id="element-558" />
                    View All Environments
                    <ChevronRight size={12} data-id="element-559" />
                  </button>
                </div>
              </div>}
          </div>)}
      </div>
    </div>;
}